import { useCallback } from 'react'
import { atom, useAtom } from 'jotai'
import S3 from 'aws-sdk/clients/s3'
import AWS from 'aws-sdk/global'
import _ from 'lodash'
import { filesize as getFileSize } from 'filesize'
import { awsConfig } from '@vzmi/ec_file_uploader_sdk'
import { safeAwait } from '@vzmi/component_ec_nodejs_util/dist/lib/FetchDataUtil'
import {
  IMAGE_UPLOADER_BASE64_SIZE_MB,
  EXCEED_LIMIT_MSG
} from '@vzmi/shp_shared_compo/constants/uploader'
import PQueue from 'p-queue'

const {
  region,
  bucket: Bucket,
  ServerSideEncryption,
  fileUploaderKey
} = awsConfig || {}

const queue = new PQueue({ concurrency: 5 })

const ABORT_ERROR_CAUSE = { code: 'ABORT_ERROR', error: null }
const abortRejectError = new Error('取消請求上傳檔案', { cause: ABORT_ERROR_CAUSE })

const isLoadingAtom = atom(false)
const errorAtom = atom(undefined)
const resultAtom = atom({})

const getExceedLimitImageFiles = async (files, onError = () => {}) => {
  const exceedLimitImageFiles = await Promise.all(
    files.map(file => new Promise((resolve) => {
      const reader = new FileReader()
      // 轉 base64 data
      reader.readAsDataURL(file)
      reader.onloadend = () => {
        const { result } = reader
        resolve({
          name: file.name,
          // bytes to MB
          ...getFileSize(result.length, { exponent: 2, output: 'object', standard: 'jedec' })
        })
      }
      reader.onerror = e => onError(e, file)
    }))
  )
    .then(base64Files => {
      return base64Files.filter(base64File => base64File.value > IMAGE_UPLOADER_BASE64_SIZE_MB)
    })
  return exceedLimitImageFiles
}

const defaultValidations = async (file = {}) => {
  const {
    name: fileName,
    type: fileType
  } = file
  if (!fileType.includes('image')) {
    return {}
  }

  const [exceedLimitError, exceedLimitFiles] = await safeAwait(getExceedLimitImageFiles([file], error => {
    throw error
  }))
  if (!_.isEmpty(exceedLimitError)) {
    return { fileName, message: exceedLimitError.message }
  }

  if (!_.isEmpty(exceedLimitFiles)) {
    return { fileName, message: EXCEED_LIMIT_MSG }
  }

  return {}
}

const defaultCustomValidations = (file) => {}

const defaultCustomUploadedImageName = (uploadedImageName, fileType = '') => {
  const ext = _.get(fileType.split('/'), '1', '')
  // 找不到副檔名就直接返回預設檔名
  if (_.isEmpty(ext) || _.isEqual(ext, fileType)) {
    return uploadedImageName
  }

  // 有副檔名不處理直接返回預設檔名
  if (_.toUpper(uploadedImageName).endsWith(_.toUpper(`.${ext}`))) {
    return uploadedImageName
  }

  // 補上副檔名
  return `${uploadedImageName}.${_.lowerCase(ext)}`
}

const useS3Upload = (
  actions,
  customValidations = defaultCustomValidations,
  customUploadedImageName = defaultCustomUploadedImageName
) => {
  const {
    getWssId,
    getCredentials,
    transformS3Object
  } = actions
  const [isLoading, setIsLoading] = useAtom(isLoadingAtom)
  const [error, setError] = useAtom(errorAtom)
  const [result, setResult] = useAtom(resultAtom)

  const refreshS3Auth = useCallback(async () => {
    const result = await safeAwait(
      Promise.all([
        getWssId(),
        getCredentials()
      ])
    )
    return result
  }, [getWssId, getCredentials])

  const trigger = useCallback(async (files, abortController) => {
    const isAborted = _.get(abortController, 'signal.aborted', false)
    if (isAborted || _.isEmpty(files)) {
      return { isLoading: false, error: undefined, result }
    }

    setIsLoading(true)
    setError()
    // 驗證檔案內容
    const asyncValidationResults = _.filter(
      await Promise.all([...files.map(defaultValidations), ...files.map(customValidations)]),
      result => !_.isEmpty(result)
    )
    const validationResults = _.flow(
      () => _.map(asyncValidationResults, result => ({ ...result, isUploading: false })),
      results => _.keyBy(results, 'fileName')
    )()
    // 預設上傳檔案狀態
    const resultWithNewUploadFiles = _.reduce(files, (newResult, file) => {
      const fileName = file.name
      const validationResult = _.get(validationResults, fileName, {})
      const isFileUploaded = (fileName in newResult)
      if (
        !isFileUploaded ||
        !_.get(newResult, fileName, {}).url
      ) {
        newResult = {
          ...newResult,
          [fileName]: { isUploading: true, fileName, ...validationResult }
        }
      }
      return newResult
    }, _.cloneDeep(result))
    setResult(resultWithNewUploadFiles)

    const {
      name: invalidFileName
    } = _.find(files, file => !!_.get(resultWithNewUploadFiles, file.name, {}).message) || {}
    // 要上傳的檔案已有驗證的錯誤訊息，停止上傳
    if (!_.isEmpty(invalidFileName)) {
      const fileMessage = _.get(resultWithNewUploadFiles, `${invalidFileName}`, {}).message
      const errorMessage = `${invalidFileName} ${fileMessage}`
      const newResult = _.flow(
        () => _.map(resultWithNewUploadFiles, file => ({ ...file, isUploading: false })),
        data => _.keyBy(data, 'fileName')
      )()
      setIsLoading(false)
      setError(errorMessage)
      setResult(newResult)
      return { isLoading: false, error: errorMessage, result: newResult }
    }

    if (_.get(abortController, 'signal.aborted', false)) {
      setIsLoading(false)
      return { isLoading: false, error: undefined, result }
    }

    // 取得 s3 upload 權限
    const [s3AuthError = {}, [wssid, credentials] = []] = await refreshS3Auth()
    if (!_.isEmpty(s3AuthError)) {
      const s3AuthErrorMessage = s3AuthError.message
      setIsLoading(false)
      setError(s3AuthErrorMessage)
      return { isLoading: false, error: s3AuthErrorMessage, result }
    }

    AWS.config.update({
      maxRetries: 2,
      region,
      credentials
    })
    const client = new S3()
    const tmpResult = {}
    // 驗證失敗檔案不上傳
    const validFiles = _.filter(files, file => !_.get(resultWithNewUploadFiles, file.name, {}).message)
    const signal = _.get(abortController, 'signal')
    const withAbortRace = (uploadPromise) => {
      if (!signal) {
        return uploadPromise
      }
      const abortPromise = new Promise((resolve, reject) => {
        const onAbort = () => {
          signal.removeEventListener('abort', onAbort)
          reject(abortRejectError)
        }
        signal.addEventListener('abort', onAbort)
      })
      // 如果 abortPromise 先 resolve，則 uploadPromise 會被中斷
      return Promise.race([uploadPromise, abortPromise])
    }
    const [s3UploadError = {}, data] = await safeAwait(
      Promise.all(
        validFiles.map(file => {
          const fileName = file.name
          // 上傳檔案到 s3
          const uploadPromise = queue.add(() => {
            return client.putObject({
              Body: file,
              Bucket,
              ContentType: file.type,
              Key: customUploadedImageName(`${fileUploaderKey}${fileName}`, file.type),
              ServerSideEncryption,
              Metadata: {
                'YahooWSSID-Authorization': wssid
              }
            })
              .promise()
              // s3api 取得 yimg url
              .then(transformS3Object)
              .then(async (resultWithYImg) => {
                const newResult = { fileName, isUploading: false, ...resultWithYImg }
                tmpResult[fileName] = newResult
                setResult({ ...resultWithNewUploadFiles, ...tmpResult, [fileName]: newResult })
                return newResult
              })
          })
          return withAbortRace(uploadPromise)
        })
      )
    )
    setIsLoading(false)
    const isAbortError = _.get(s3UploadError, 'cause.code') === ABORT_ERROR_CAUSE.code
    if (isAbortError) {
      queue.clear()
    }
    const s3UploadErrorMessage = isAbortError ? '取消上傳檔案' : _.get(s3UploadError, 'message')
    setError(s3UploadErrorMessage)
    const newResult = (isAbortError || !_.isEmpty(s3UploadError))
      ? result
      : { ...result, ..._.keyBy(data, 'fileName') }
    setResult(newResult)
    return { isLoading: false, error: s3UploadErrorMessage, result: newResult }
  }, [setIsLoading, setError, customValidations, result, setResult, refreshS3Auth, customUploadedImageName, transformS3Object])

  const reset = useCallback(() => {
    setIsLoading(false)
    setError()
    setResult({})
  }, [setIsLoading, setError, setResult])

  return {
    isLoading,
    error,
    trigger,
    result,
    reset
  }
}

export default useS3Upload
